import React, { useState, useEffect, useRef } from 'react';
import { Button, TextField, Typography, List, ListItem, InputAdornment, CssBaseline, Box, IconButton, Container, useMediaQuery } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';

import ToastStr from '../request/toastStr';
import { COLORS } from '../shared/Theme';
import { SIcon } from '../shared/Icon';
import { admin as v_admin, basic, Values, SNACKBAR_TIME, edu } from '../request/values';
import AudioPlayer from '../components/AudioPlayer';
import { formatMoneyAmount } from '../locales/formatModule';

const SERVER = basic.server;

// 테마 컬러 상수화 (대시보드 전용)
const THEME = {
	WATER_BLUE: '#1291d9',
	PUMPKIN: '#ef7d00',
	BACKGROUND: '#f4f7fa',
	SURFACE: '#ffffff',
	TEXT_MAIN: '#333333',
	TEXT_SUB: '#888888',
};

const useStyles = makeStyles((theme: any) => ({
	// --- 기존 입력 화면 스타일 복구 ---
	originalBackground: {
		position: 'absolute',
		top: 0,
		width: '100vw',
		height: '100vh',
		background: `url("/images/background.png")`,
	},
	container1: {
		padding: 0,
		height: '85vh',
		marginTop: '15vh',
	},
	back: {
		position: 'absolute',
		float: 'left',
		width: '20%',
		maxWidth: '75px',
	},
	inputSection: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		fontSize: '3vw',
		fontWeight: 'bold',
		fontFamily: 'NanumB',
		marginTop: '3vh',
	},
	originalFormCard: {
		width: '100%',
		maxWidth: 240,
		padding: '20px 16px',
		[theme.breakpoints.up('md')]: {
			maxWidth: 400,
			padding: '40px 32px',
		},
		margin: '0 auto',
		borderRadius: 16,
		backgroundColor: '#fff',
		boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
	},
	originalFormTitle: {
		...theme.typography.subtitle1,
		fontWeight: 700,
		marginBottom: theme.spacing(2),
		color: '#595959',
	},
	originalField: {
		marginBottom: theme.spacing(3),
		'& .MuiOutlinedInput-root': {
			borderRadius: 12,
		},
	},
	tFieldRoot: {
		'& .MuiInputLabel-outlined': {
			backgroundColor: '#fff',
			padding: '0 4px',
			zIndex: 1,
		},
		'& .MuiOutlinedInput-notchedOutline': {
			borderColor: '#cfd4d9',
		},
		'& .MuiInputLabel-root.Mui-focused': {
			color: '#868686',
		},
		'& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
			transform: 'translate(14px, -9px) scale(0.75)',
		},
		'& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
			borderColor: '#868686',
			borderWidth: 1,
		},
		'& .MuiInputLabel-outlined.MuiFormLabel-filled + .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
			borderColor: '#868686',
			borderWidth: 1.5,
		},
		'& .MuiOutlinedInput-root.Mui-focused .MuiInputAdornment-root': {
			color: '#868686',
		},
		'& .MuiInputLabel-outlined.MuiFormLabel-filled + .MuiOutlinedInput-root .MuiInputAdornment-root': {
			color: '#868686',
		},
	},
	unitAdornment: {
		color: '#8a8a8a',
		marginLeft: theme.spacing(1),
		marginRight: theme.spacing(1),
	},
	tipBox: {
		textAlign: 'center',
		color: '#8a8a8a',
		backgroundColor: '#f5f6f7',
		borderRadius: 12,
		padding: theme.spacing(1.5),
		margin: theme.spacing(1, 0, 2),
		fontWeight: 600,
		fontSize: '0.9rem',
	},
	originalStartButton: {
		width: '100%',
		height: '48px',
		borderRadius: 12,
		backgroundColor: `${COLORS.WATER_BLUE}`,
		color: '#fff',
		fontWeight: 700,
		'&:hover': {
			backgroundColor: '#2d7fae',
		},
	},
	buttonLabel: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		fontSize: '2.3vh',
		fontFamily: 'NanumB',
		lineHeight: '21px',
	},

	// --- 신규 랭킹 대시보드 화면 스타일 ---
	dashboardBackground: {
		position: 'relative',
		width: '100vw',
		height: '100vh',
		backgroundColor: THEME.BACKGROUND,
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden',
		fontFamily: 'NanumSquare, NanumB, sans-serif',
	},
	dashboardLayout: {
		display: 'grid',
		gridTemplateColumns: '240px 1fr',
		height: '100vh',
		padding: theme.spacing(3),
		gap: theme.spacing(3),
		[theme.breakpoints.down('sm')]: {
			gridTemplateColumns: '1fr',
			gridTemplateRows: 'auto 1fr',
			padding: theme.spacing(1.5),
			gap: theme.spacing(1.5),
			height: '100%',
			minHeight: 0,
		},
	},
	sidePanel: {
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(2),
		[theme.breakpoints.down('sm')]: {
			gap: theme.spacing(1.5),
		},
	},
	mainPanel: {
		display: 'flex',
		flexDirection: 'column',
		backgroundColor: THEME.SURFACE,
		borderRadius: 24,
		boxShadow: '0 8px 32px rgba(0,0,0,0.05)',
		overflow: 'hidden',
		[theme.breakpoints.down('sm')]: {
			borderRadius: 16,
			minHeight: 0,
		},
	},
	infoCard: {
		backgroundColor: THEME.SURFACE,
		borderRadius: 20,
		padding: theme.spacing(3, 2),
		boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		gap: theme.spacing(1.5),
		[theme.breakpoints.down('sm')]: {
			position: 'relative',
			borderRadius: 16,
			padding: theme.spacing(2, 1.5),
			gap: theme.spacing(1),
		},
	},
	timerText: {
		fontSize: '2.4rem',
		fontWeight: 900,
		color: THEME.WATER_BLUE,
		fontFamily: 'monospace',
		[theme.breakpoints.down('sm')]: {
			fontSize: '1.75rem',
		},
	},
	qrWrapper: {
		padding: theme.spacing(1.5),
		backgroundColor: '#fff',
		borderRadius: 16,
		border: `2px solid ${THEME.BACKGROUND}`,
		[theme.breakpoints.down('sm')]: {
			padding: theme.spacing(1),
			borderRadius: 12,
		},
	},
	mobileQrRow: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		gap: theme.spacing(0.75),
	},
	classCodeBlock: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		gap: 2,
	},
	classCodeLabel: {
		fontFamily: 'NanumB',
		fontWeight: 700,
		fontSize: 12,
		color: THEME.TEXT_SUB,
		lineHeight: 1.2,
	},
	classCodeValue: {
		fontWeight: 800,
		color: THEME.WATER_BLUE,
		letterSpacing: 1,
	},
	mobileBackButton: {
		display: 'none',
		[theme.breakpoints.down('sm')]: {
			display: 'inline-flex',
			position: 'absolute',
			top: 15,
			left: 10,
			zIndex: 2,
			padding: 6,
			color: THEME.TEXT_MAIN,
		},
	},
	musicCard: {
		backgroundColor: THEME.SURFACE,
		borderRadius: 20,
		padding: theme.spacing(2),
		boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		[theme.breakpoints.down('sm')]: {
			display: 'none',
		},
	},
	musicTitle: {
		color: THEME.PUMPKIN,
		fontWeight: 800,
		marginBottom: theme.spacing(1),
		fontSize: '0.95rem',
	},
	musicControls: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1),
	},
	songName: {
		marginTop: theme.spacing(1.5),
		fontSize: '0.8rem',
		color: THEME.TEXT_SUB,
		textAlign: 'center',
	},
	// --- 단상(Trophy) 스타일 ---
	trophySection: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'flex-end',
		padding: theme.spacing(4, 0, 5, 0),
		gap: theme.spacing(4),
		minHeight: 240,
		background: `linear-gradient(to bottom, #ffffff, ${THEME.BACKGROUND})`,
		[theme.breakpoints.down('sm')]: {
			display: 'none',
		},
	},
	trophyCard: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		padding: theme.spacing(2, 3),
		backgroundColor: THEME.SURFACE,
		borderRadius: 20,
		boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
		position: 'relative',
		transition: 'transform 0.3s ease',
	},
	rank1Card: {
		transform: 'scale(1.15)',
		zIndex: 2,
		border: `2px solid ${THEME.PUMPKIN}`,
		boxShadow: `0 12px 32px rgba(239, 125, 0, 0.15)`,
		marginBottom: theme.spacing(2),
	},
	rank2Card: {
		border: `2px solid #e0e0e0`,
	},
	rank3Card: {
		border: `2px solid #e0e0e0`,
	},
	trophyImg: {
		width: 70,
		height: 'auto',
		marginBottom: theme.spacing(1.5),
		filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.1))',
	},
	rank1Img: {
		width: 90,
	},
	companyNameBadge: {
		fontWeight: 800,
		fontSize: '1.1rem',
		color: THEME.TEXT_MAIN,
		textAlign: 'center',
		maxWidth: 120,
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
	rank1Name: {
		color: THEME.PUMPKIN,
		fontSize: '1.25rem',
	},
	// --- 랭킹 테이블 스타일 (자동 스크롤 영역) ---
	tableContainer: {
		flex: 1,
		padding: theme.spacing(0, 4, 4, 4),
		overflowY: 'auto',
		minHeight: 0,
		// 스크롤바 숨기기 (선택사항, 깔끔함을 위해)
		'&::-webkit-scrollbar': {
			width: '0px',
			background: 'transparent',
		},
		[theme.breakpoints.down('sm')]: {
			padding: theme.spacing(0, 1.5, 2, 1.5),
		},
	},
	tableHeader: {
		display: 'grid',
		gridTemplateColumns: '60px 2fr 1.5fr 30px 1.5fr 30px 1.5fr',
		padding: theme.spacing(2),
		borderBottom: `2px solid ${THEME.BACKGROUND}`,
		color: THEME.TEXT_SUB,
		fontWeight: 700,
		textAlign: 'center',
		position: 'sticky',
		top: 0,
		backgroundColor: THEME.SURFACE,
		zIndex: 10,
		[theme.breakpoints.down('sm')]: {
			display: 'none',
		},
	},
	mobileTableHeader: {
		display: 'none',
		[theme.breakpoints.down('sm')]: {
			display: 'grid',
			flexShrink: 0,
			gridTemplateColumns: '44px 1fr 1fr',
			padding: theme.spacing(1.5, 1),
			margin: theme.spacing(0, 1.5),
			borderBottom: `2px solid ${THEME.BACKGROUND}`,
			color: THEME.TEXT_SUB,
			fontWeight: 700,
			textAlign: 'center',
			backgroundColor: THEME.SURFACE,
			fontSize: '0.8rem',
		},
	},
	mobileListScroll: {
		display: 'none',
		[theme.breakpoints.down('sm')]: {
			display: 'block',
			flex: 1,
			minHeight: 0,
			overflowY: 'auto',
			padding: theme.spacing(0, 1.5, 2, 1.5),
			backgroundColor: THEME.SURFACE,
			'&::-webkit-scrollbar': {
				width: '0px',
				background: 'transparent',
			},
		},
	},
	listItem: {
		display: 'grid',
		gridTemplateColumns: '60px 2fr 1.5fr 30px 1.5fr 30px 1.5fr',
		padding: theme.spacing(2),
		marginBottom: theme.spacing(1),
		backgroundColor: '#fff',
		borderRadius: 12,
		boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
		alignItems: 'center',
		textAlign: 'center',
		[theme.breakpoints.down('sm')]: {
			display: 'none',
		},
	},
	mobileListItem: {
		display: 'none',
		[theme.breakpoints.down('sm')]: {
			display: 'grid',
			gridTemplateColumns: '44px 1fr 1fr',
			padding: theme.spacing(1.5, 1),
			marginBottom: theme.spacing(0.75),
			backgroundColor: '#fff',
			borderRadius: 10,
			boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
			alignItems: 'center',
			textAlign: 'center',
			gap: theme.spacing(0.5),
		},
	},
	mobileCompanyName: {
		fontWeight: 700,
		fontSize: '0.9rem',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		textAlign: 'left',
		paddingLeft: theme.spacing(0.5),
	},
	rankBadge: {
		width: 32,
		height: 32,
		borderRadius: '50%',
		backgroundColor: THEME.BACKGROUND,
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		fontWeight: 800,
		color: THEME.TEXT_MAIN,
		margin: '0 auto',
		[theme.breakpoints.down('sm')]: {
			width: 28,
			height: 28,
			fontSize: '0.85rem',
		},
	},
	capitalText: {
		fontWeight: 800,
		color: THEME.WATER_BLUE,
		[theme.breakpoints.down('sm')]: {
			fontSize: '0.85rem',
		},
	},
	mathSymbol: {
		color: THEME.TEXT_SUB,
		fontWeight: 800,
	},
	endingContainer: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
		alignItems: 'center',
		height: '100vh',
		gap: theme.spacing(4),
	},
	endingTitle: {
		fontSize: '3rem',
		fontWeight: 900,
		color: THEME.TEXT_MAIN,
		[theme.breakpoints.down('sm')]: {
			fontSize: '1.75rem',
			textAlign: 'center',
			padding: theme.spacing(0, 2),
		},
	},
	blindContent: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
		alignItems: 'center',
		height: '100%',
		padding: theme.spacing(2),
	},
	blindImage: {
		width: '45%',
		[theme.breakpoints.down('sm')]: {
			width: '70%',
			maxWidth: 220,
		},
	},
	// --- 재료 주문 마감 뉴스 티커 ---
	newsTicker: {
		position: 'absolute',
		bottom: theme.spacing(4),
		left: theme.spacing(3),
		right: theme.spacing(3),
		height: 72,
		backgroundColor: COLORS.RED,
		display: 'flex',
		alignItems: 'center',
		overflow: 'hidden',
		zIndex: 100,
		boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
		borderRadius: 4,
		[theme.breakpoints.down('sm')]: {
			bottom: theme.spacing(1.5),
			left: theme.spacing(1.5),
			right: theme.spacing(1.5),
			height: 48,
		},
	},
	newsTickerTrack: {
		flex: 1,
		overflow: 'hidden',
		height: '100%',
		position: 'relative',
		display: 'flex',
		alignItems: 'center',
	},
	newsTickerText: {
		position: 'absolute',
		left: 0,
		whiteSpace: 'nowrap',
		color: '#ffffff',
		fontWeight: 900,
		fontSize: '1.75rem',
		animation: '$newsMarquee 14s linear forwards',
		[theme.breakpoints.down('sm')]: {
			fontSize: '1rem',
		},
	},
	'@keyframes newsMarquee': {
		'0%': {
			left: '100%',
			transform: 'translateX(0)',
		},
		'100%': {
			left: '0%',
			transform: 'translateX(-100%)',
		},
	},
}));

const MATERIAL_ORDER_CLOSE_MINUTES = 10;
const NEWS_TICKER_INTERVAL_MS = 30 * 1000;

const OperClassRun: React.FC<any> = (props) => {
	const classes = useStyles();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const { t } = useTranslation('oper');

	const [hours, setHours] = useState<number>(0);
	const [minutes, setMinutes] = useState<number>(0);
	const [seconds, setSeconds] = useState<number>(0);

	const [nowHours, setNowHours] = useState<any | null>();
	const [nowMin, setNowMin] = useState<any | null>();
	const [nowSec, setNowSec] = useState<any | null>();

	const [endHours, setEndHours] = useState<any | null>();
	const [endMin, setEndMin] = useState<any | null>();
	const [endSec, setEndSec] = useState<any | null>();

	const [hideTime, setHideTime] = useState<any>(20);

	const [mTypeNum, setMTypeNum] = useState<any>(0);
	const [dTypeNum, setDTypeNum] = useState<any>(1);
	const { enqueueSnackbar } = useSnackbar();

	const [rank, setRank] = useState([
		{ company_name: '', stock: 0, loan: 0, loan_interest: 0, capital: 0, order_inventory: 0, used_mat: 0, license_stock: 0, material_stock: 0, material_loan_stock: 0 },
		{ company_name: '', stock: 0, loan: 0, loan_interest: 0, capital: 0, order_inventory: 0, used_mat: 0, license_stock: 0, material_stock: 0, material_loan_stock: 0 },
		{ company_name: '', stock: 0, loan: 0, loan_interest: 0, capital: 0, order_inventory: 0, used_mat: 0, license_stock: 0, material_stock: 0, material_loan_stock: 0 },
	]);

	const [timerFlag, setTimerFlag] = useState<any | null>(false);
	const [songFlag, setSongFlag] = useState<any | null>(false);
	const history = useHistory();
	const { oper } = props;

	const state: any = history.location.state;
	const classCode = state.code;
	const wakeLockRef = useRef<WakeLockSentinel | null>(null);
	const isMountedRef = useRef(true);

	// 자동 스크롤을 위한 Ref
	const listRef = useRef<HTMLDivElement>(null);

	const songs = [
		'../bgm/innovation-vision-corporate.mp3',
		'../bgm/inspiring-epic-dubstep.mp3',
		'../bgm/radiant-horizons.mp3',
		'../bgm/summer-adventures.mp3',
		'../bgm/summer-dance-party.mp3',
		'../bgm/summer-dance-spark.mp3',
		'../bgm/sunrise-groove.mp3',
		'../bgm/sunshine.mp3',
		'../bgm/upbeat-acoustic.mp3',
	];

	const songs_name = ['innovation vision', 'inspiring epic', 'radiant horizon', 'summer adventures', 'summer dance party', 'summer dance spark', 'sunrise groove', 'sunshine', 'upbeat acoustic'];

	const [playingSrc, setPlayingSrc] = useState<string | null>(null);
	const [currentSongIndex, setCurrentSongIndex] = useState<number>(0);
	const [newsTickerVisible, setNewsTickerVisible] = useState(false);
	const [newsTickerKey, setNewsTickerKey] = useState(0);

	const handleSongEnd = () => {
		setCurrentSongIndex((prevIndex) => (prevIndex + 1) % songs.length);
	};

	const handlePlay = (src: string) => {
		if (playingSrc && playingSrc !== src) {
			setPlayingSrc(null);
			setTimeout(() => setPlayingSrc(src), 100);
		} else {
			setPlayingSrc(playingSrc === src ? null : src);
		}
	};

	const handlePrevSong = () => setCurrentSongIndex((prevIndex) => (prevIndex === 0 ? songs.length - 1 : prevIndex - 1));
	const handleNextSong = () => setCurrentSongIndex((prevIndex) => (prevIndex + 1) % songs.length);

	const setEndTime = (event: any) => {
		if (endHours == null || endMin == null) {
			enqueueSnackbar(ToastStr.CLASS_END_TIME_STR, { variant: 'warning', autoHideDuration: SNACKBAR_TIME });
		} else if (mTypeNum === 0) {
			enqueueSnackbar(ToastStr.CLASS_TYPE_NUM_STR, { variant: 'warning', autoHideDuration: SNACKBAR_TIME });
		} else {
			let et = parseInt(endHours) * 3600 + parseInt(endMin) * 60 + parseInt(endSec);
			let nt = parseInt(nowHours) * 3600 + parseInt(nowMin) * 60 + parseInt(nowSec);
			let temp = et - nt;

			if (temp <= 0) {
				enqueueSnackbar(ToastStr.CLASS_END_TIME_PAST_STR, { variant: 'warning', autoHideDuration: SNACKBAR_TIME });
				return;
			}

			const endLocal = new Date();
			endLocal.setHours(parseInt(endHours, 10) || 0, parseInt(endMin, 10) || 0, parseInt(endSec || 0, 10) || 0, 0);

			postClassTypeRatio();
			if (classCode !== '12345') {
				postClassTimeUpdate(endLocal.toISOString());
			}

			setHours(Math.floor(temp / 3600));
			temp = temp % 3600;
			setMinutes(Math.floor(temp / 60));
			temp = temp % 60;
			setSeconds(temp);

			setTimerFlag(true);
		}
	};

	const valueChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { id, value } = e.target;
		const values = value.replace(/[^0-9]/g, '');
		if (id === 'hours') {
			setEndHours(values);
			setEndSec(0);
		} else if (id === 'minutes') {
			setEndMin(values);
			setEndSec(0);
		} else if (id === 'hideTime') {
			setHideTime(values);
		} else if (id === 'dType') {
			setDTypeNum(values);
		} else if (id === 'mType') {
			setMTypeNum(values);
		}
	};

	const movePage = () => history.push({ pathname: '/operation/OpenRanking', state: { code: classCode } });

	const movePageWithFinish = async () => {
		if (classCode !== '12345') {
			await postClassFinish();
		}
		movePage();
	};

	const postClassTypeRatio = () => {
		let url = new URL(basic.server + v_admin.admin + v_admin.type_update);
		axios({
			method: 'POST',
			withCredentials: true,
			url: url.toString(),
			data: { classCode: classCode, mTypeNum: mTypeNum, dTypeNum: dTypeNum },
		})
			.then((res) => res.data)
			.then((data: any) => {
				if (data.code == Values.SUCCESS_CODE) {
					enqueueSnackbar(ToastStr.CLASS_START_SUCCESS_STR, { variant: 'success', autoHideDuration: SNACKBAR_TIME });
				} else {
					enqueueSnackbar(data.msg || ToastStr.CLASS_START_FAIL_STR, { variant: 'warning', autoHideDuration: SNACKBAR_TIME });
				}
			});
	};

	const postClassFinish = () => {
		return axios({
			method: 'POST',
			withCredentials: true,
			url: SERVER + v_admin.admin + v_admin.update_status,
			data: { classCode, classStatus: 'finish' },
		})
			.then((res) => res.data)
			.then((data: any) => data?.code == Values.SUCCESS_CODE)
			.catch(() => false);
	};

	const postClassTimeUpdate = (endDate: string) => {
		let url = new URL(basic.server + edu.path + edu.class_time_update);
		return axios({
			method: 'POST',
			withCredentials: true,
			url: url.toString(),
			data: { class_code: classCode, end_date: endDate },
		})
			.then((res) => res.data)
			.then((data: any) => {
				if (data.code != Values.SUCCESS_CODE) {
					enqueueSnackbar(ToastStr.CLASS_TIME_UPDATE_FAIL_STR, { variant: 'warning', autoHideDuration: SNACKBAR_TIME });
				}
			});
	};

	const getRankContent = () => {
		axios({
			method: 'POST',
			data: { class_code: oper.classCode, classVersion: oper.classVersion },
			withCredentials: true,
			url: SERVER + edu.path + edu.class_rank_content,
		})
			.then((res) => res.data)
			.then((data: any) => {
				if (!isMountedRef.current) return;
				if (data.code == Values.SUCCESS_CODE) {
					setRank(data.rank);
				}
			})
			.catch(() => {
				// 언마운트/네트워크 오류 시 무시
			});
	};

	useEffect(() => {
		isMountedRef.current = true;
		getRankContent();
		const tick = setInterval(() => {
			getRankContent();
		}, 1000 * 15);
		return () => {
			isMountedRef.current = false;
			clearInterval(tick);
		};
	}, []);

	useEffect(() => {
		if (!isMountedRef.current) return;
		// 모바일에서는 BGM 자동재생하지 않음
		if (isMobile) {
			setPlayingSrc(null);
			return;
		}
		setPlayingSrc(songs[currentSongIndex]);
	}, [currentSongIndex, isMobile]);

	useEffect(() => {
		if (timerFlag === true && hours <= 0 && minutes <= 0 && seconds <= 0) {
			if (!songFlag) setSongFlag(true);
			if (playingSrc) setPlayingSrc(null);
		}
	}, [hours, minutes, seconds, timerFlag]);

	useEffect(() => {
		const countdown = setInterval(() => {
			if (!isMountedRef.current) return;

			const currentNow = new Date();
			setNowHours(currentNow.getHours());
			setNowMin(currentNow.getMinutes());
			setNowSec(currentNow.getSeconds());

			let et = parseInt(endHours) * 3600 + parseInt(endMin) * 60 + parseInt(endSec);
			let nt = parseInt(nowHours) * 3600 + parseInt(nowMin) * 60 + parseInt(nowSec);
			let temp = et - nt;

			setHours(Math.floor(temp / 3600));
			temp = temp % 3600;
			setMinutes(Math.floor(temp / 60));
			temp = temp % 60;
			setSeconds(temp);
		}, 1000);
		return () => clearInterval(countdown);
	}, [nowHours, nowMin, nowSec, endHours, endMin, endSec]);

	useEffect(() => {
		const requestWakeLock = async () => {
			try {
				const lock: WakeLockSentinel = await navigator.wakeLock.request('screen');
				if (!isMountedRef.current) {
					lock.release().catch(() => undefined);
					return;
				}
				wakeLockRef.current = lock;
			} catch (err: any) {
				console.error(`${err.name}, ${err.message}`);
			}
		};
		const releaseWakeLock = () => {
			if (wakeLockRef.current !== null) {
				wakeLockRef.current.release().then(() => {
					wakeLockRef.current = null;
				});
			}
		};
		requestWakeLock();
		return () => {
			releaseWakeLock();
		};
	}, []);

	// --- 랭킹 리스트 자동 스크롤 로직 (데스크톱만) ---
	useEffect(() => {
		let scrollInterval: NodeJS.Timeout;

		if (timerFlag && !isMobile && listRef.current) {
			scrollInterval = setInterval(() => {
				if (listRef.current) {
					const { scrollTop, scrollHeight, clientHeight } = listRef.current;
					// 스크롤이 끝에 도달하면 맨 위로 초기화, 아니면 1px씩 이동
					if (scrollTop + clientHeight >= scrollHeight - 1) {
						listRef.current.scrollTop = 0;
					} else {
						listRef.current.scrollTop += 1;
					}
				}
			}, 50); // 0.05초마다 1px 이동 (부드러운 스크롤 속도)
		}

		return () => clearInterval(scrollInterval);
	}, [timerFlag, rank, isMobile]);

	const isTimeUp = hours <= 0 && minutes <= 0 && seconds <= 0;
	const showEnding = timerFlag === true && isTimeUp && songFlag === true;
	// 종료 화면 직전까지는 블라인드 유지 (0초 순간 랭킹 스포 방지)
	const isBlind = timerFlag === true && !showEnding && (isTimeUp || (hours == 0 && minutes < parseInt(hideTime)));

	// --- 재료 주문 마감 뉴스 티커 (블라인드 구간, 30초마다) ---
	useEffect(() => {
		if (!isBlind) {
			setNewsTickerVisible(false);
			return;
		}

		const showTicker = () => {
			if (!isMountedRef.current) return;
			setNewsTickerKey((prev) => prev + 1);
			setNewsTickerVisible(true);
		};

		showTicker();
		const tickerInterval = setInterval(showTicker, NEWS_TICKER_INTERVAL_MS);
		return () => clearInterval(tickerInterval);
	}, [isBlind]);

	const getMaterialOrderTickerMessage = () => {
		if (minutes <= MATERIAL_ORDER_CLOSE_MINUTES) {
			return t('OperClassRun.materialOrderClosed');
		}
		const minutesUntilClose = minutes - MATERIAL_ORDER_CLOSE_MINUTES;
		return t('OperClassRun.materialOrderClosingSoon', { n: minutesUntilClose });
	};

	// --- 렌더링 영역 ---

	// 1. 종료 화면
	if (showEnding) {
		return (
			<div className={classes.dashboardBackground}>
				<CssBaseline />
				<Container className={classes.endingContainer}>
					<Typography className={classes.endingTitle}>{t('OperClassRun.endedTitle')}</Typography>
					<Button variant="contained" onClick={() => movePageWithFinish()} className={classes.originalStartButton} style={{ width: 300 }}>
						{t('OperClassRun.showRanking')}
					</Button>
				</Container>
			</div>
		);
	}

	// 2. 랭킹 대시보드 화면
	else if (hours != null && minutes != null && seconds != null && timerFlag === true) {
		return (
			<div className={classes.dashboardBackground} data-testid="oper-main-ranking-dashboard">
				<CssBaseline />
				<div className={classes.dashboardLayout}>
					{/* 왼쪽 패널 (모바일: 상단 — 타이머 / QR·클래스코드) */}
					<div className={classes.sidePanel}>
						<div className={classes.infoCard}>
							{isMobile && (
								<IconButton className={classes.mobileBackButton} onClick={() => history.goBack()} aria-label="back" size="small">
									<SIcon type="Back" viewBox="0 0 10.823 16.84" />
								</IconButton>
							)}
							<Typography className={classes.timerText}>
								{hours}:{minutes < 10 ? `0${minutes}` : minutes}:{seconds < 10 ? `0${seconds}` : seconds}
							</Typography>
							{isMobile ? (
								<div className={classes.mobileQrRow}>
									<div className={classes.qrWrapper}>
										<QRCode size={72} value={`${basic.base_url}/classEnter?classCode=${classCode}`} />
									</div>
									<div className={classes.classCodeBlock}>
										<Typography className={classes.classCodeLabel}>{t('OperClassRun.classCodeLabel')}</Typography>
										<Typography variant="subtitle1" className={classes.classCodeValue}>
											{classCode}
										</Typography>
									</div>
								</div>
							) : (
								<>
									<div className={classes.qrWrapper}>
										<QRCode size={120} value={`${basic.base_url}/classEnter?classCode=${classCode}`} />
									</div>
									<div className={classes.classCodeBlock}>
										<Typography className={classes.classCodeLabel}>{t('OperClassRun.classCodeLabel')}</Typography>
										<Typography variant="subtitle2" className={classes.classCodeValue} style={{ fontWeight: 700, letterSpacing: 0 }}>
											{classCode}
										</Typography>
									</div>
								</>
							)}
						</div>

						{!isMobile && (
							<div className={classes.musicCard}>
								<Typography className={classes.musicTitle}>{t('OperClassRun.playlist')}</Typography>
								<div className={classes.musicControls}>
									<IconButton onClick={handlePrevSong} size="small">
										<img src="../images/backward_button.png" width={20} alt="prev" />
									</IconButton>
									<AudioPlayer src={songs[currentSongIndex]} isPlaying={playingSrc === songs[currentSongIndex]} onPlay={handlePlay} onEnded={handleSongEnd} />
									<IconButton onClick={handleNextSong} size="small">
										<img src="../images/forward_button.png" width={20} alt="next" />
									</IconButton>
								</div>
								<Typography className={classes.songName}>
									{currentSongIndex + 1}. {songs_name[currentSongIndex]}
								</Typography>
							</div>
						)}
					</div>

					{/* 오른쪽 메인 패널 (모바일: 하단 — 리스트만) */}
					<div className={classes.mainPanel}>
						{isBlind ? (
							<div className={classes.blindContent}>
								<img src="/images/playImage.png" alt="Blind" className={classes.blindImage} />
								<Typography variant={isMobile ? 'subtitle1' : 'h5'} style={{ marginTop: 20, fontWeight: 700, color: THEME.TEXT_SUB, textAlign: 'center' }}>
									지금부터 랭킹이 가려집니다...
								</Typography>
							</div>
						) : (
							<>
								{!isMobile && (
									<div className={classes.trophySection}>
										{/* 2등 */}
										<div className={`${classes.trophyCard} ${classes.rank2Card}`}>
											<img src="/images/ranking/silver.webp" className={classes.trophyImg} alt="Silver Medal" />
											<div className={classes.companyNameBadge}>{rank[1]?.company_name || '-'}</div>
										</div>
										{/* 1등 */}
										<div className={`${classes.trophyCard} ${classes.rank1Card}`}>
											<img src="/images/ranking/gold.webp" className={`${classes.trophyImg} ${classes.rank1Img}`} alt="Gold Medal" />
											<div className={`${classes.companyNameBadge} ${classes.rank1Name}`}>{rank[0]?.company_name || '-'}</div>
										</div>
										{/* 3등 */}
										<div className={`${classes.trophyCard} ${classes.rank3Card}`}>
											<img src="/images/ranking/bronze.webp" className={classes.trophyImg} alt="Bronze Medal" />
											<div className={classes.companyNameBadge}>{rank[2]?.company_name || '-'}</div>
										</div>
									</div>
								)}

								{/* 데스크톱: 헤더+리스트 함께 스크롤(sticky) / 모바일: 헤더 고정 + 리스트만 스크롤 */}
								{isMobile ? (
									<>
										<div className={classes.mobileTableHeader}>
											<div>{t('OperClassRun.rank')}</div>
											<div>{t('OperClassRun.companyName')}</div>
											<div>{t('OperClassRun.capital')}</div>
										</div>
										<div className={classes.mobileListScroll} ref={listRef}>
											<List disablePadding>
												{rank.map((item: any, index: number) => (
													<AppListItem
														key={index}
														index={index}
														name={item.company_name}
														stock={item.stock}
														loan={item.loan}
														loan_interest={item.loan_interest}
														capital={item.capital}
														material_stock={item.material_stock}
														material_loan_stock={item.material_loan_stock}
														license_stock={item.license_stock}
													/>
												))}
											</List>
										</div>
									</>
								) : (
									<div className={classes.tableContainer} ref={listRef}>
										<div className={classes.tableHeader}>
											<div>{t('OperClassRun.rank')}</div>
											<div>{t('OperClassRun.companyName')}</div>
											<div>{t('OperClassRun.asset')}</div>
											<div></div>
											<div>{t('OperClassRun.debt')}</div>
											<div></div>
											<div>{t('OperClassRun.capital')}</div>
										</div>
										<List disablePadding>
											{rank.map((item: any, index: number) => (
												<AppListItem
													key={index}
													index={index}
													name={item.company_name}
													stock={item.stock}
													loan={item.loan}
													loan_interest={item.loan_interest}
													capital={item.capital}
													material_stock={item.material_stock}
													material_loan_stock={item.material_loan_stock}
													license_stock={item.license_stock}
												/>
											))}
										</List>
									</div>
								)}
							</>
						)}
					</div>
				</div>

				{isBlind && newsTickerVisible && (
					<div className={classes.newsTicker} data-testid="oper-material-order-news-ticker">
						<div className={classes.newsTickerTrack}>
							<span key={newsTickerKey} className={classes.newsTickerText} onAnimationEnd={() => setNewsTickerVisible(false)}>
								{getMaterialOrderTickerMessage()}
							</span>
						</div>
					</div>
				)}
			</div>
		);
	}

	// 3. 기존 원본 입력 화면
	else {
		return (
			<div className={classes.originalBackground} data-testid="oper-ranking-info-input-dashboard">
				<Container component="main" className={classes.container1}>
					<CssBaseline />
					<Box className={classes.back} onClick={() => history.goBack()}>
						<IconButton
							style={{
								width: '100%',
								height: '100%',
								margin: 'auto',
							}}
						>
							<img style={{ width: '25%' }} src="/images/title/back.webp" />
						</IconButton>
					</Box>
					<div className={classes.inputSection}>
						<div className={classes.originalFormCard}>
							<Typography className={classes.originalFormTitle}>{t('OperClassRun.endTimeTitle')}</Typography>

							<TextField
								id="hours"
								name="endHours"
								className={`${classes.originalField} ${classes.tFieldRoot}`}
								onChange={valueChangeHandler}
								value={endHours ?? ''}
								placeholder="14"
								variant="outlined"
								fullWidth
								type="number"
								autoComplete="off"
								inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', 'data-testid': 'oper-ranking-info-input-hours' }}
								InputProps={{
									endAdornment: (
										<InputAdornment position="end">
											<span className={classes.unitAdornment}>{t('OperClassRun.hour')}</span>
										</InputAdornment>
									),
								}}
							/>

							<TextField
								id="minutes"
								name="endMin"
								className={`${classes.originalField} ${classes.tFieldRoot}`}
								onChange={valueChangeHandler}
								value={endMin ?? ''}
								placeholder="30"
								variant="outlined"
								fullWidth
								type="number"
								autoComplete="off"
								inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', 'data-testid': 'oper-ranking-info-input-minutes' }}
								InputProps={{
									endAdornment: (
										<InputAdornment position="end">
											<span className={classes.unitAdornment}>{t('OperClassRun.minute')}</span>
										</InputAdornment>
									),
								}}
							/>
							<div className={classes.tipBox}>{t('OperClassRun.endTimeTip')}</div>
							<Typography className={classes.originalFormTitle}>{t('OperClassRun.companyCountTitle')}</Typography>
							<TextField
								id="mType"
								name="mType"
								className={`${classes.originalField} ${classes.tFieldRoot}`}
								onChange={valueChangeHandler}
								placeholder="4"
								label={t('OperClassRun.manufacturingCompanyCount')}
								variant="outlined"
								fullWidth
								autoComplete="off"
								type="number"
								inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', 'data-testid': 'oper-ranking-info-input-mType' }}
								InputProps={{
									endAdornment: (
										<InputAdornment position="end">
											<span className={classes.unitAdornment}>{t('OperClassRun.countUnit')}</span>
										</InputAdornment>
									),
								}}
							/>
							<TextField
								id="dType"
								name="mType"
								className={`${classes.originalField} ${classes.tFieldRoot}`}
								onChange={valueChangeHandler}
								placeholder="1"
								value={dTypeNum}
								label={t('OperClassRun.distributionCompanyCount')}
								variant="outlined"
								autoComplete="off"
								fullWidth
								type="number"
								inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', 'data-testid': 'oper-ranking-info-input-dType' }}
								InputProps={{
									endAdornment: (
										<InputAdornment position="end">
											<span className={classes.unitAdornment}>{t('OperClassRun.countUnit')}</span>
										</InputAdornment>
									),
								}}
							/>

							<Button fullWidth variant="contained" className={classes.originalStartButton} onClick={(e) => setEndTime(e)} data-testid="oper-ranking-info-input-submit-btn">
								<Typography className={classes.buttonLabel}>{t('OperClassRun.start')}</Typography>
							</Button>
						</div>
					</div>
				</Container>
			</div>
		);
	}
};

const AppListItem: React.FC<any> = (props) => {
	const classes = useStyles();
	const { index, name, stock, loan, loan_interest, capital, material_stock, material_loan_stock, license_stock } = props;
	const capitalAmount = formatMoneyAmount(capital + license_stock);

	return (
		<>
			{/* 데스크톱: 자산 / 부채 / 자본 상세 행 */}
			<ListItem className={classes.listItem} data-testid={`oper-main-ranking-${name}`}>
				<div className={classes.rankBadge}>{index + 1}</div>
				<div style={{ fontWeight: 700 }}>{name}</div>
				<div>{formatMoneyAmount(stock + material_stock + license_stock)}</div>
				<div className={classes.mathSymbol}>-</div>
				<div>{formatMoneyAmount(loan + loan_interest + material_loan_stock)}</div>
				<div className={classes.mathSymbol}>=</div>
				<div className={classes.capitalText} data-testid={`oper-main-ranking-${name}-capital`}>
					{capitalAmount}
				</div>
			</ListItem>
			{/* 모바일: 순위 / 회사명 / 자본만 */}
			<ListItem className={classes.mobileListItem}>
				<div className={classes.rankBadge}>{index + 1}</div>
				<div className={classes.mobileCompanyName}>{name}</div>
				<div className={classes.capitalText}>{capitalAmount}</div>
			</ListItem>
		</>
	);
};

export default OperClassRun;
